import { useTheme } from '@mui/material/styles';

export const getTourConfig = (theme) => {
    const isDark = theme.palette.mode === 'dark';

    return {
        dashboard: [
            {
                target: 'body',
                content: 'Welcome to your Dashboard! This is your central hub for monitoring all compliance activities.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#dashboard-metrics',
                content: 'Here you can see key performance indicators and quick stats at a glance.',
            },
            {
                target: '#dashboard-charts',
                content: 'Visualize your compliance trends and data over time.',
            },
            {
                target: '#dashboard-tasks',
                content: 'Keep track of your pending and upcoming tasks.',
            },
            {
                target: '#sidebar-nav',
                content: 'Use the sidebar to navigate to different modules like Documents, Audits, and Users.',
                placement: 'right',
            }
        ],
        documents: [
            {
                target: 'body',
                content: 'Welcome to the Document Management module! This is where you maintain full control over your organization\'s documentation.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-document-btn',
                content: 'Upload new files or create documents from templates here. You can also organize them into folders.',
            },
            {
                target: '#document-filters',
                content: 'Filter documents by status (Draft, Approved, etc.), ISO standard, or category to find exactly what you need.',
            },
            {
                target: '#documents-table',
                content: 'Your document library. Perform actions like viewing, editing, or starting a review/approval workflow.',
            }
        ],
        audits: [
            {
                target: 'body',
                content: 'Plan and manage your internal and external audits here to ensure compliance with your chosen standards.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-audit-btn',
                content: 'Create a new audit, specify the scope, and assign auditors and auditees.',
            },
            {
                target: '#audit-filters',
                content: 'Keep track of planned, in-progress, and completed audits using these filters.',
            },
            {
                target: '#audits-list',
                content: 'Manage your audit checklists, record findings, and track non-conformities directly from here.',
            }
        ],
        capa: [
            {
                target: 'body',
                content: 'Manage Non-Conformities and Corrective/Preventive Actions (CAPA) to drive continuous improvement.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-nc-btn',
                content: 'Record new non-conformities identified during audits, incidents, or customer complaints.',
            },
            {
                target: '#nc-filters',
                content: 'Monitor the status and severity of all open and closed issues.',
            },
            {
                target: '#nc-list',
                content: 'Manage the full lifecycle of each issue: from root cause analysis to action implementation and verification.',
            }
        ],
        risks: [
            {
                target: 'body',
                content: 'Identify, analyze, and mitigate risks and opportunities within your organization.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-risk-btn',
                content: 'Register a new risk or opportunity. Define its probability and impact to calculate the risk score.',
            },
            {
                target: '#risk-filters',
                content: 'Visualize risks by category, severity, or status.',
            },
            {
                target: '#risk-list',
                content: 'Track mitigation actions and regular risk reviews to ensure your risk profile stays within acceptable limits.',
            }
        ],
        tasks: [
            {
                target: 'body',
                content: 'Manage and track tasks for process improvement and regular operations.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-task-btn',
                content: 'Create a new task, assign it to a team member, and set a due date.',
            },
            {
                target: '#task-filters',
                content: 'Filter tasks by status, priority, or department.',
            },
            {
                target: '#tasks-list',
                content: 'Track the progress of all improvement initiatives and recurring tasks.',
            }
        ],
        nonconformities: [
            {
                target: 'body',
                content: 'Record and manage non-conformities identified during audits or daily operations.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-nc-btn',
                content: 'Log a new non-conformity. Specify the source, severity, and involved standard clauses.',
            },
            {
                target: '#nc-filters',
                content: 'Track trends in non-conformities by category or status.',
            },
            {
                target: '#nc-list',
                content: 'Manage the investigation and immediate containment actions for each issue.',
            }
        ],
        iso_clauses: [
            {
                target: 'body',
                content: 'Map your organizational processes and documents to ISO standard clauses.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#clause-filters',
                content: 'Select the standard and clause to view its requirements and mapping status.',
            },
            {
                target: '#clause-mapping-list',
                content: 'Manage the link between specific requirements and your evidence documents.',
            }
        ],
        change_management: [
            {
                target: 'body',
                content: 'Control and document changes to processes, systems, or resources.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-change-btn',
                content: 'Initiate a new change request. Define the scope, impact, and required approvals.',
            },
            {
                target: '#change-filters',
                content: 'Monitor the status of all requested and active changes.',
            },
            {
                target: '#change-list',
                content: 'Track change implementation plans, risk assessments, and final verification.',
            }
        ],
        quality_alerts: [
            {
                target: 'body',
                content: 'Issue and track urgent quality alerts and safety notifications.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-alert-btn',
                content: 'Create a new alert to notify staff about critical issues or process changes.',
            },
            {
                target: '#alert-list',
                content: 'Track receipt and acknowledgment of alerts by relevant personnel.',
            }
        ],
        anonymous_reporting: [
            {
                target: 'body',
                content: 'Manage anonymous reports and whistleblower cases securely.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#anon-filters',
                content: 'Sort reports by status, priority, or category while maintaining reporter anonymity.',
            },
            {
                target: '#anon-list',
                content: 'Investigate reported concerns and track resolution actions.',
            }
        ],
        customer_complaints: [
            {
                target: 'body',
                content: 'Track and resolve customer complaints to improve service quality.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-complaint-btn',
                content: 'Register a new customer complaint. Capture all relevant details and evidence.',
            },
            {
                target: '#complaint-filters',
                content: 'Monitor complaints by status, customer, or severity.',
            },
            {
                target: '#complaints-list',
                content: 'Manage the resolution workflow and link to corrective actions if needed.',
            }
        ],
        quality_objectives: [
            {
                target: 'body',
                content: 'Define and monitor your organization\'s Quality Objectives and KPIs.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-objective-btn',
                content: 'Set new SMART objectives and link them to specific standards or departments.',
            },
            {
                target: '#objectives-list',
                content: 'Track progress towards your goals with real-time KPI updates.',
            }
        ],
        management_reviews: [
            {
                target: 'body',
                content: 'Plan and document your Management Reviews for ISO compliance.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-review-btn',
                content: 'Schedule a new review meeting. Organise the agenda and invite participants.',
            },
            {
                target: '#reviews-list',
                content: 'Record minutes, track action items, and store review records.',
            }
        ],
        training_management: [
            {
                target: 'body',
                content: 'Systematically manage staff training and competency requirements.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-training-btn',
                content: 'Create new training modules or programs. Define requirements and materials.',
            },
            {
                target: '#training-filters',
                content: 'Monitor training compliance across departments or job roles.',
            },
            {
                target: '#training-list',
                content: 'Track attendance, assessments, and certificate expiration.',
            }
        ],
        permissions: [
            {
                target: 'body',
                content: 'Configure granular access permissions for your organization.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#permissions-filters',
                content: 'Search through the wide range of available permission nodes.',
            },
            {
                target: '#permissions-list',
                content: 'Assign permissions to roles to build a robust security architecture.',
            }
        ],
        departments: [
            {
                target: 'body',
                content: 'Define your organizational structure and process ownership.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-dept-btn',
                content: 'Add new departments or processes to reflect your operational reality.',
            },
            {
                target: '#depts-list',
                content: 'Structure your hierarchy and assign responsible managers.',
            }
        ],
        teams: [
            {
                target: 'body',
                content: 'Manage your organizational departments and link them to core processes.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-team-btn',
                content: 'Add a new department. You can assign a code and link it to a specific process from your library.',
            },
            {
                target: '#teams-list',
                content: 'Monitor your departments and their status here.',
            }
        ],
        org_chart: [
            {
                target: 'body',
                content: 'Visualize your organizational structure and reporting lines.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#org-chart-container',
                content: 'Explore the hierarchy of departments, teams, and job positions.',
            }
        ],
        job_positions: [
            {
                target: 'body',
                content: 'Manage definitions and requirements for all job positions.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-job-btn',
                content: 'Create a new job position. Define its title, department, and reporting structure.',
            },
            {
                target: '#job-filters',
                content: 'Filter positions by department or status.',
            },
            {
                target: '#jobs-list',
                content: 'Manage job descriptions and competency requirements for each role.',
            }
        ],
        job_libraries: [
            {
                target: 'body',
                content: 'Manage competencies, skill matrices, and employee assessments.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#library-tabs',
                content: 'Switch between Competency Library, Skill Matrix, and Assessment criteria.',
            },
            {
                target: '#library-content',
                content: 'Manage the master data that powers your job descriptions and training plans.',
            }
        ],
        task_templates: [
            {
                target: 'body',
                content: 'Create and manage reusable templates for recurring tasks and processes.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-template-btn',
                content: 'Build a new template with predefined steps, assignees, and checklists.',
            },
            {
                target: '#templates-list',
                content: 'Standardize your operations by deploying these templates across the organization.',
            }
        ],
        job_analytics: [
            {
                target: 'body',
                content: 'Insights into your workforce competency and training gaps.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#analytics-filters',
                content: 'Drill down into specific departments or roles to identify training needs.',
            },
            {
                target: '#analytics-content',
                content: 'Visualize competency coverage and identify critical skill shortages.',
            }
        ],
        settings: [
            {
                target: 'body',
                content: 'Configure your organization\'s platform preferences and integrations.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#settings-tabs',
                content: 'Manage Profile, Security, Notifications, and Organization-wide settings.',
            }
        ],
        support_tickets: [
            {
                target: 'body',
                content: 'Get help from our support team or manage internal requests.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-ticket-btn',
                content: 'Open a new support request. Provide details and screenshots for faster resolution.',
            },
            {
                target: '#tickets-list',
                content: 'Track the status and history of all your support interactions.',
            }
        ],
        users: [
            {
                target: 'body',
                content: 'Manage your team members, their roles, and access permissions.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#new-user-btn',
                content: 'Invite new users to the platform and assign them to specific job positions.',
            },
            {
                target: '#user-filters',
                content: 'Quickly find users and manage their account status.',
            },
            {
                target: '#users-list',
                content: 'Assign roles like Org Admin, Compliance Manager, or Auditor to control access levels.',
            }
        ],
        platform_guide: [
            {
                target: 'body',
                content: 'Welcome to the MarQ ISO Management System! We\'ve prepared this interactive guide to help you navigate and master your new platform.',
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#sidebar-nav',
                content: 'This sidebar is your primary navigation. You can switch between modules like Documents, Audits, CAPAs, and more.',
                placement: 'right',
            },
            {
                target: '#dashboard-metrics',
                content: 'Your Dashboard provides a real-time overview of your compliance health, including pending tasks and critical alerts.',
            },
            {
                target: '#global-search-btn',
                content: 'Need to find something fast? Use Global Search to instantly locate documents, records, or users across the entire system.',
            },
            {
                target: '#dashboard-documents-card',
                content: 'The Document Management module is where you store and manage all your ISO-relevant documentation with full version control.',
            },
            {
                target: '#dashboard-audits-card',
                content: 'Plan, execute, and track internal and external audits easily. Stay ahead of your compliance schedule.',
            },
            {
                target: '#dashboard-capa-card',
                content: 'Manage Corrective and Preventive Actions (CAPA) to drive continuous improvement within your organization.',
            },
            {
                target: '#user-settings-menu',
                content: 'Access your profile and organization settings here. You can manage users, roles, and system preferences.',
                placement: 'bottom',
            },
            {
                target: 'body',
                content: 'You\'re all set! If you ever need to revisit this guide, just click the "Platform Guide" button in the top header.',
                placement: 'center',
            }
        ]
    };
};
